import { FormControl, FormControlLabel, Grid, makeStyles, Radio, RadioGroup, Step, StepContent, StepLabel, Stepper } from '@material-ui/core';
import React, {useContext, useEffect, useMemo, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import AddItems from './AddItems';
import PickupSelector from './Addresses/PickupSelector';
import ShipToSelector from './Addresses/ShipToSelector';
import ControlContainer from './components/ControlContainer';
import { useIsSmall } from './components/MediaHelpers';
import ExchangeItems from './ExchangeItems';
import OrderConfirmation from './OrderConfirmation';
import { OrderContext } from './OrderContext';
import OrderSummary from './OrderSummary';
import { CancelButton, NextButton } from './StepperButtons';

type StepDefinition = {
    label: string,
    description?: string,
    showFor?: 'Exchange' | 'Return',
    component: React.ReactNode
}

const OrderTypeSelector = () => {
    const { orderType, setOrderType, customer, returnsEnabled, exchangesEnabled, setActiveStep, activeStep } = useContext(OrderContext)
    const { t } = useTranslation()
    const nextButton = useRef<HTMLButtonElement>(document.createElement('button'))

    const [exchangeText, returnText] = customer?.policyChkText?.split('\n') || ['', '']
    
    useEffect(() => {
        if (!returnsEnabled || !exchangesEnabled) {
            setOrderType(returnsEnabled ? 'Return' : 'Exchange')
            setActiveStep(activeStep + 1)
            window.scrollTo(0, 0)
        }
    }, [returnsEnabled, exchangesEnabled])

    return (
        <div>
            <FormControl component="fieldset">
                <RadioGroup row aria-label="orderType" name="row-radio-buttons-group" value={orderType} onChange={e => setOrderType(e.target.value as 'Return' | 'Exchange')}>
                    <FormControlLabel value="Exchange" control={<Radio color="primary" disabled={!exchangesEnabled} />} label={t('return_for_exchange')} />
                    <FormControlLabel value="Return" control={<Radio color="primary" disabled={!returnsEnabled} />} label={t('return_for_credit')} />
                </RadioGroup>
            </FormControl>

            <br />
            <br />

            <div style={{ fontSize: '1.2em' }}>
                {orderType === 'Exchange' ? exchangeText : returnText}
            </div>

            <ControlContainer>
                <NextButton ref={nextButton} />
                <CancelButton />
            </ControlContainer>
        </div>
    )
}

export const useStyles = makeStyles(theme => ({
    stepper: {
        background: theme.palette.background.default
    }
}))

const ReturnStepper = () => {
    const { activeStep, setActiveStep, orderType, session } = useContext(OrderContext)
    const classes = useStyles()
    const isSmall = useIsSmall()
    const { t } = useTranslation()

    const steps: StepDefinition[] = useMemo(() => [
        { label: t('select_type'), description: t('return_or_exhange'), component: <OrderTypeSelector /> },
        { label: t('add_return_items'), component: <AddItems /> },
        { label: t('select'), description: t('item_receive_in_exchange'), showFor: 'Exchange', component: <ExchangeItems /> },
        { label: t('pickup_address'), component: <PickupSelector /> },
        { label: t('exchange_shipping_address'), showFor: 'Exchange', component: <ShipToSelector /> },
        { label: t('summary'), component: <OrderSummary /> },
        { label: t('confirmation'), component: <OrderConfirmation /> },
    ], [t])

    const orderSteps = useMemo(() => steps.filter(s => s.showFor === undefined || s.showFor === orderType), [orderType, steps])

    const handleClick = (step: number) => () => {
        if (step < activeStep && activeStep !== orderSteps.length - 1)
            setActiveStep(step)
    }

    useEffect(() => {
        if (activeStep < orderSteps.length - 1 && session === JSON.parse(sessionStorage.getItem('orderCreate') || '{}')['session']) setActiveStep(orderSteps.length - 1)
    }, [activeStep, orderSteps.length, session, setActiveStep])

    return (
        <Grid item container spacing={3}>
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
                <Stepper activeStep={activeStep} orientation={isSmall ? 'horizontal' : 'vertical'} className={classes.stepper} style={{ padding: isSmall ? 0 : undefined }}>
                    {orderSteps.map((step, index) => (
                        <Step key={index} onClick={handleClick(index)}>
                            <StepLabel>
                                {!isSmall ? step.label : null}
                            </StepLabel>
                            {!isSmall && <StepContent>
                                {step.description}
                            </StepContent>}
                        </Step>
                    ))}
                </Stepper>
            </Grid>

            <Grid item xs={12} sm={12} md={9} lg={9}>
                <div style={{ marginTop: isSmall ? 0 : 90 }}>
                    {orderSteps[activeStep].component}
                </div>
            </Grid>
        </Grid>
    )
}

export default ReturnStepper
